/**
 * Les primitives communes aux graphiques (`ARCHITECTURE_V2.md` §6.3).
 *
 * 🔴 **Extraites de `WeightChart` parce qu'un second graphique est arrivé**
 * *(29/08/2026, la charge d'un exercice dans le temps)*, et parce que le §6.3
 * l'annonçait : « les formes nécessaires sont peu nombreuses — série
 * temporelle, barres empilées par semaine, nuage de points. **Trois primitives
 * réutilisées dix fois, ce n'est pas dix graphiques.** »
 *
 * ⚠️ **Extraites à l'identique, sans une ligne de comportement changée.**
 * `WeightChart` est éprouvé sur l'appareil depuis l'étape 8 ; le refaire à
 * l'occasion d'un ajout, c'est le casser à l'occasion d'un ajout. Ce qui reste
 * chez lui est ce qui lui appartient : sa fenêtre d'objectif, ses quatre plages,
 * ses deux séries.
 *
 * 🔴 **Ce qui devait absolument ne pas être recopié, c'est `niceTicks`.** Deux
 * algorithmes de graduation divergeraient à bas bruit — un axe en kilos ronds
 * ici, en fractions de l'étendue là — et le symptôme serait deux graphiques du
 * même projet qui ne se lisent pas pareil. Personne ne signale ça ; on se dit
 * qu'on lit mal.
 */

/** Le jour d'une date `YYYY-MM-DD`, en jours depuis l'époque — l'axe du temps. */
export function toEpochDay(date: string): number {
  return Math.round(Date.parse(`${date}T00:00:00Z`) / 86_400_000);
}

/**
 * Graduations sur des valeurs **rondes**, pas sur des fractions de l'étendue.
 *
 * ⚠️ Une première version plaçait les traits à 10 %, 50 % et 90 % de la
 * hauteur : c'était simple, et ça donnait des repères comme « 92,1 · 85,1 ·
 * 78,1 » — trois nombres qu'aucun lecteur ne retient et qui ne se comparent à
 * rien. Constaté en relisant une capture, pas au typecheck : la géométrie était
 * juste, la lecture ne l'était pas. Un axe de poids se lit en kilos ronds.
 *
 * Le pas est choisi dans une échelle fixe pour que trois ou quatre traits
 * couvrent l'étendue — au-delà l'axe devient une grille, en deçà il ne situe
 * plus rien.
 */
export function niceTicks(min: number, max: number): number[] {
  const span = max - min;
  const steps = [0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50];
  const step = steps.find((candidate) => span / candidate <= 4) ?? 100;

  const ticks: number[] = [];
  const first = Math.ceil(min / step) * step;
  for (let value = first; value <= max + 1e-9; value += step) {
    ticks.push(Math.round(value * 100) / 100);
  }

  // Étendue si étroite qu'aucun multiple ne tombe dedans : on retombe sur les
  // bornes plutôt que de rendre un axe sans aucun repère.
  if (ticks.length === 0) return [Math.round(((min + max) / 2) * 10) / 10];
  return ticks;
}

/**
 * Le point le plus proche d'une abscisse — **le doigt désigne une date, jamais
 * un trait de 2 px**.
 *
 * ⚠️ Extraite de `WeightChart` le 05/09/2026, quand `ExerciseChart` a eu besoin
 * du même geste. Elle y vivait depuis l'étape 8 avec sa justification, qui vaut
 * pour les deux : « c'est ce qui rend le geste utilisable au doigt sur 393 px,
 * où viser un point serait illusoire ».
 *
 * ⚠️ **Aucun rayon d'accroche.** Un seuil (« à moins de 20 px ») ferait des
 * zones mortes entre les points, où toucher ne répondrait rien — sur un
 * graphique dont on ne connaît pas la densité à l'avance, c'est un contrôle qui
 * marche une fois sur deux. Le plus proche est toujours désigné.
 *
 * `null` sur une série vide : il n'y a rien à désigner.
 */
export function nearestIndex(abscisses: readonly number[], pointerX: number): number | null {
  let best: number | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  abscisses.forEach((abscisse, index) => {
    const distance = Math.abs(abscisse - pointerX);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = index;
    }
  });
  return best;
}

/**
 * Où poser une infobulle pour qu'elle **ne puisse pas** sortir du cadre.
 *
 * 🔴 **C'est la réponse à la seule objection sérieuse contre les infobulles**,
 * consignée dans `WeightChart` depuis l'étape 8 : « sur 393 px, une infobulle
 * près du doigt sort du cadre dès qu'on approche d'un bord et demanderait une
 * logique de recadrage ». La voici, écrite une fois pour tous les graphiques.
 *
 * 🔴 **Sans mesurer la bulle.** Lire sa largeur (un `ResizeObserver`, comme
 * `useElementWidth`) marcherait, au prix d'une image où elle est mal placée
 * avant d'être mesurée — donc d'un sursaut à chaque point touché. On l'ancre
 * plutôt **selon le tiers** où tombe le point :
 *
 * | Le point est… | Ancrage | La bulle s'étend… |
 * |-|-|-|
 * | tiers gauche | son bord gauche sur le point | vers la droite |
 * | milieu | son centre sur le point | des deux côtés |
 * | tiers droit | son bord droit sur le point | vers la gauche |
 *
 * ⚠️ **La garantie est arithmétique, et elle dépend d'une largeur maximale de
 * 60 %** (`.n-chart-tip`, `nourri-screens.css`). Au pire des cas — bord gauche
 * posé à ⅓ — le bord droit tombe à ⅓ + 60 % = 93 % de la largeur. Centré, la
 * bulle occupe au plus [⅓ − 30 %, ⅔ + 30 %] = [3 %, 97 %]. **Augmenter cette
 * largeur maximale sans refaire ce calcul rouvre le défaut.**
 *
 * ⚠️ **Verticalement, deux cas seulement** : sous le point s'il est dans la
 * moitié haute, au-dessus sinon. Toujours au-dessus, une bulle posée sur un
 * point haut sortirait par le haut de la carte — que `.n-card` découpe
 * (`overflow: hidden`), donc sans même le dire.
 */
export interface TooltipAnchor {
  left: number | null;
  right: number | null;
  top: number | null;
  bottom: number | null;
  /** La bulle doit être décalée de la moitié de sa largeur (`translateX`). */
  centered: boolean;
}

export function tooltipAnchor(
  x: number,
  y: number,
  boxWidth: number,
  boxHeight: number,
  gap: number,
): TooltipAnchor {
  const anchor: TooltipAnchor = { left: null, right: null, top: null, bottom: null, centered: false };

  if (x < boxWidth / 3) {
    anchor.left = Math.round(x);
  } else if (x > (boxWidth * 2) / 3) {
    anchor.right = Math.round(boxWidth - x);
  } else {
    anchor.left = Math.round(x);
    anchor.centered = true;
  }

  if (y < boxHeight / 2) {
    anchor.top = Math.round(y + gap);
  } else {
    anchor.bottom = Math.round(boxHeight - y + gap);
  }

  return anchor;
}

/**
 * Ouvre une fenêtre verticale autour de valeurs, **jamais plate**.
 *
 * ⚠️ **Un seul point, ou plusieurs points à la même valeur, ne doit pas
 * produire une échelle plate** : la division par `max − min` vaudrait zéro et
 * tout le tracé partirait à `NaN`. On ouvre alors la fenêtre d'une unité de
 * part et d'autre, ce qui donne une ligne horizontale au milieu du cadre — la
 * lecture juste pour « une seule mesure ».
 *
 * ⚠️ Extraite parce que le piège est le même sur les deux graphiques, et que
 * c'est exactement le genre de garde qu'on oublie de recopier.
 */
export function verticalWindow(values: readonly number[]): { min: number; max: number } {
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const plat = rawMax - rawMin < 0.2;
  return {
    min: plat ? rawMin - 1 : rawMin - (rawMax - rawMin) * 0.12,
    max: plat ? rawMax + 1 : rawMax + (rawMax - rawMin) * 0.12,
  };
}
